import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Check, FileText, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { VideoPlayer } from "@/components/VideoPlayer";
import { BranchDivider, ScriptMark } from "@/components/Brand";
import { openDocument } from "@/pages/portal/PortalHome";

export default function PortalVideos() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["portal", "series"], queryFn: async () => (await api.get("/portal/series")).data });
  const [currentId, setCurrentId] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [celebrate, setCelebrate] = useState(null);

  useEffect(() => {
    if (!data?.videos?.length || currentId) return;
    const next = data.videos.find((v) => !v.completed) || data.videos[0];
    setCurrentId(next.id);
  }, [data, currentId]);

  const current = data?.videos?.find((v) => v.id === currentId);

  const onProgress = async (position, duration, event) => {
    if (!current) return;
    try {
      const { data: r } = await api.post(`/portal/videos/${current.id}/progress`, { position_seconds: position, duration_seconds: duration, event });
      qc.setQueryData(["portal", "series"], (old) => old && { ...old, videos: old.videos.map((v) => (v.id === current.id ? { ...v, percent: r.percent, completed: r.completed } : v)) });
      if (r.completed && !current.completed) {
        toast.success(`"${current.title}" complete.`);
        qc.invalidateQueries({ queryKey: ["portal"] });
        if (r.certificate) setCelebrate(r);
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  if (isLoading) return <p className="p-10 text-ink-500" role="status">Loading your series…</p>;
  if (!data?.available) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6" data-testid="videos-locked">
        <h1 className="text-4xl">Video Series</h1>
        <div className="surface mt-8 max-w-xl p-6"><Lock className="h-6 w-6 text-ink-500" aria-hidden="true" /><p className="mt-3 text-lg">{data?.reason}</p><Link to="/portal" className="btn-secondary mt-5">Back To My Care Journey</Link></div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6" data-testid="portal-videos">
      <Link to="/portal" className="link text-sm">My Care Journey</Link>
      <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
        <div><p className="text-sm font-semibold text-terracotta-600">{data.series.area}</p><h1 className="mt-1 text-4xl sm:text-5xl" data-testid="series-title">{data.series.title}</h1></div>
        <p className="text-ink-500" data-testid="series-progress">{data.completed_count} of {data.total} complete</p>
      </div>
      <p className="mt-3 max-w-[65ch] text-ink-500">{data.series.description}</p>
      <div className="mt-3 h-1.5 w-full max-w-xl bg-sage-400/40"><div className="h-full bg-olive-900 transition-[width]" style={{ width: `${(data.completed_count / Math.max(data.total, 1)) * 100}%` }} /></div>

      {celebrate && (
        <section className="confirm-pop mt-8 border-2 border-terracotta-600 bg-cream-100 p-6 sm:p-8" data-testid="certificate-banner">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-3xl">Your Certificate Is Ready</h2>
              <p className="mt-2 max-w-[55ch]">You finished every session. Certificate <span className="font-mono">{celebrate.certificate.number}</span> is in your portal{celebrate.advanced ? ", and your In-Person Intake is now open." : celebrate.waiting_for?.length ? ". The In-Person Intake opens when your partner finishes too." : "."}</p>
              <div className="mt-4 flex flex-wrap gap-3">
                <button type="button" className="btn-primary" onClick={() => openDocument(celebrate.certificate.document_id)} data-testid="certificate-download">Download Certificate</button>
                {celebrate.advanced && <Link to="/portal/in-person-intake" className="btn-terracotta" data-testid="certificate-next-intake">Start In-Person Intake</Link>}
              </div>
            </div>
            <ScriptMark className="h-16 w-auto shrink-0 sm:h-20" />
          </div>
        </section>
      )}
      {!celebrate && data.certificate && (
        <p className="surface mt-8 flex flex-wrap items-center justify-between gap-3 p-4" data-testid="certificate-existing">
          <span>Certificate <span className="font-mono">{data.certificate.number}</span> issued. {data.case_state === "certificate_issued" ? "Your In-Person Intake is open." : data.partners.some((p) => !p.complete) ? "Waiting for your partner to finish the series." : ""}</span>
          <span className="flex gap-3">{data.case_state === "certificate_issued" && <Link to="/portal/in-person-intake" className="btn-terracotta min-h-[40px]">In-Person Intake</Link>}<button type="button" className="btn-secondary min-h-[40px]" onClick={() => openDocument(data.certificate.document_id)}>Download</button></span>
        </p>
      )}
      {data.partners.length > 0 && (
        <p className="mt-4 text-sm text-ink-500" data-testid="partner-progress">{data.partners.map((p) => `${p.first_name} ${p.complete ? "has finished the series" : "is still watching"}`).join(" · ")}</p>
      )}

      <div className="mt-10 grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-8">
          {current && (
            <section aria-labelledby="current-video" data-testid="current-video">
              <VideoPlayer video={current} onProgress={onProgress} />
              <div className="mt-5 flex flex-wrap items-start justify-between gap-3">
                <div><h2 id="current-video" className="text-2xl sm:text-3xl">{current.title}</h2><p className="mt-2 max-w-[60ch] text-ink-500">{current.description}</p></div>
                <span className={`shrink-0 border px-2.5 py-1 text-sm font-semibold ${current.completed ? "border-olive-900 bg-olive-900 text-cream-50" : "border-sage-400"}`} data-testid="current-video-status">{current.completed ? "Complete" : `${current.percent}% watched`}</span>
              </div>
              <p className="help mt-3">Watch to the end (at least 90%) to mark a session complete. Skipping ahead does not count.</p>
              <button type="button" className="btn-quiet mt-3" onClick={() => setShowTranscript((s) => !s)} aria-expanded={showTranscript} data-testid="transcript-toggle"><FileText className="h-4 w-4" aria-hidden="true" /> {showTranscript ? "Hide Transcript" : "Transcript"}</button>
              {showTranscript && <div className="surface mt-3 max-h-80 overflow-y-auto p-5 text-base leading-relaxed whitespace-pre-wrap" data-testid="transcript">{current.transcript || "No transcript yet."}</div>}
            </section>
          )}
        </div>
        <aside className="lg:col-span-4">
          <h2 className="text-xl">Sessions</h2>
          <ol className="mt-3 divide-y divide-sage-400/50 border-y border-sage-400/50" data-testid="video-list">
            {data.videos.map((v, i) => (
              <li key={v.id}>
                <button type="button" onClick={() => { setCurrentId(v.id); setShowTranscript(false); }} aria-current={v.id === currentId ? "true" : undefined} className={`flex w-full items-start gap-3 px-2 py-3 text-left hover:bg-cream-100 ${v.id === currentId ? "bg-cream-100" : ""}`} data-testid={`video-item-${v.id}`}>
                  <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-xs ${v.completed ? "border-olive-900 bg-olive-900 text-cream-50" : "border-sage-400 text-ink-500"}`} aria-hidden="true">{v.completed ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : i + 1}</span>
                  <span className="min-w-0 flex-1"><span className="block font-semibold">{v.title}</span><span className="block text-sm text-ink-500">{v.completed ? (v.accommodation ? "Marked complete" : "Complete") : v.percent ? `${v.percent}% watched` : v.is_placeholder ? "Placeholder" : `${Math.round(v.duration_seconds / 60)} min`}</span></span>
                </button>
              </li>
            ))}
          </ol>
          <BranchDivider tone="terracotta" className="mt-8" />
          <p className="mt-4 text-sm text-ink-500">Every published video includes captions and a transcript. If a video is hard for you to use, reach out and the Care Director can mark it complete as an accommodation.</p>
        </aside>
      </div>
    </div>
  );
}
